import { Dictionary } from '@tableau/data-structures';
import { SiteUrls } from '@tableau/shared-urls';
import { IDataQualityIndicator } from '@tableau/vizportal-webclient-api/lib/api/types/IDataQualityIndicator';
import { Formatters, Messages } from '../../../../../gen-src/Localize';
import { DataQualityCertificationAssetFragment, DataQualityWarningFragment, DataQualityWarningAssetFragment, DataQualityCertificationFragment } from '../../../../../gen-src/queries';
import { DataQualityType, stringToDataQualityType } from '../dataQualityWarnings/DataQualityType';
import { assertUnreachable, filterOutNullAndUndefined } from '../utils/TypescriptHelpers';
import { DataQualityDetailPanelSide, DataQualityIndicatorAssetType, DataQualityIndicatorKind, IndicatorSubjectArea, RenderIndicatorAssetProps, RenderIndicatorProps, RenderIndicatorsProps, } from './DataQualityIndicatorSharedHelpers';

export type DataQualityCertificationGraphqlOrIndicator = GraphDataQualityCertification | DataQualityWarningIndicator;

/** Data Quality "Indicators" (same thing as Warnings) that come from the VizPortal API */
/**
 * @deprecated TFS-1478812
*/
export type DataQualityWarningIndicator = IDataQualityIndicator & WarnableAsset;

/**
 * @deprecated TFS-1478812
*/
export type AssetMarkedByIndicator = {
  readonly assetName: string;
  readonly assetUrl: (urls: SiteUrls) => string;
  readonly assetType: DataQualityIndicatorAssetType;
};

/**
 * @deprecated TFS-1478812
*/
export type GraphDataQualityWarning = DataQualityWarningFragment & DataQualityWarningAssetFragment;
/**
 * @deprecated TFS-1478812
*/
export type GraphDataQualityCertification = DataQualityCertificationFragment & DataQualityCertificationAssetFragment;
/**
 * @deprecated TFS-1478812
*/
export type VizportalDataQualityIndicator = AssetMarkedByIndicator & {
  readonly indicators: ReadonlyArray<IDataQualityIndicator>;
};
/**
 * @deprecated TFS-1478812
*/
export type CertifiableAsset = AssetMarkedByIndicator
/**
 * @deprecated TFS-1478812
*/
export type WarnableAsset = AssetMarkedByIndicator

/**
 * @deprecated TFS-1478812
*/
export type RenderIndicatorsBaseProps = {
  readonly source?: string;
  readonly iconOnly: boolean;
  readonly urls?: SiteUrls;
  readonly side?: DataQualityDetailPanelSide;
  readonly newTab?: boolean;
  readonly linksDisabled?: boolean;
  readonly tabIndex?: number;
  readonly subjectArea: IndicatorSubjectArea;
  readonly stackingRootId?: string;
}

/**
 * @deprecated TFS-1478812
*/
export type CertificationStatusProps = ({
  readonly kind: 'graph';
  readonly anchoredAsset: DataQualityCertificationAssetFragment;
  readonly anchoredCertifications: ReadonlyArray<DataQualityCertificationFragment>;
} | {
  readonly kind: 'vizportal';
  readonly anchoredAsset: AssetMarkedByIndicator;
  readonly anchoredCertifications: ReadonlyArray<IDataQualityIndicator>;
}) & RenderIndicatorsBaseProps;

/**
 * @deprecated TFS-1478812
*/
export type WarningStatusProps = ({
  readonly kind: 'graph';
  readonly anchoredAsset: DataQualityWarningAssetFragment | null;
  readonly anchoredWarnings: ReadonlyArray<DataQualityWarningFragment>;
} | {
  readonly kind: 'vizportal';
  readonly anchoredAsset: AssetMarkedByIndicator | null;
  readonly anchoredWarnings: ReadonlyArray<IDataQualityIndicator>;
}) & {
  readonly anchoredChildWarnings?: ReadonlyArray<DataQualityWarningFragment & DataQualityWarningAssetFragment>;
  readonly upstreamWarnings: ReadonlyArray<GraphDataQualityWarning>;
  readonly iconOnly: boolean;
  readonly side?: DataQualityDetailPanelSide;
} & RenderIndicatorsBaseProps;

/**
 * @deprecated TFS-1478812
*/
export function onThisAssetMessage(assetType: DataQualityIndicatorAssetType): string {
  if (!assetType) {
    return Messages.DataQualityContainerAnchored();
  }

  switch (assetType) {
    case 'Column': return Messages.DataQualityIndicatorContainer_onthis_asset_column();
    case 'CloudFile':
    case 'DatabaseServer':
    case 'File':
    case 'WebDataConnector': return Messages.DataQualityIndicatorContainer_onthis_asset_database();
    case 'VirtualConnectionTable':
    case 'DatabaseTable': return Messages.DataQualityIndicatorContainer_onthis_asset_table();
    case 'Flow': return Messages.DataQualityIndicatorContainer_onthis_asset_flow();
    case 'PublishedDatasource': return Messages.DataQualityIndicatorContainer_onthis_asset_datasource();
    case 'VirtualConnection': return Messages.DataQualityIndicatorContainer_onthis_asset_connection();
    default:
      assertUnreachable(assetType);
      return '';
  }
}

/**
 * @deprecated TFS-1478812
*/
export function upstreamOfThisAssetMessage(assetType: IndicatorSubjectArea): string {
  if (!assetType) {
    return Messages.DataQualityContainerUpstream();
  }

  switch (assetType) {
    case 'Metric': return Messages.DataQualityIndicatorContainer_upstreamof_asset_metric();
    case 'CloudFile':
    case 'DatabaseServer':
    case 'File':
    case 'Column':
    case 'WebDataConnector': return Messages.DataQualityIndicatorContainer_upstreamof_asset_database();
    case 'Workbook': return Messages.DataQualityIndicatorContainer_upstreamof_asset_workbook();
    case 'VirtualConnectionTable':
    case 'DatabaseTable': return Messages.DataQualityIndicatorContainer_upstreamof_asset_table();
    case 'VirtualConnection': return Messages.DataQualityIndicatorContainer_upstreamof_asset_connection();
    case 'Flow': return Messages.DataQualityIndicatorContainer_upstreamof_asset_flow();
    case 'EmbeddedDatasource':
    case 'PublishedDatasource': return Messages.DataQualityIndicatorContainer_upstreamof_asset_datasource();
    case 'View': return Messages.DataQualityIndicatorContainer_upstreamof_asset_view();
    case 'Field': return Messages.DataQualityIndicatorContainer_upstreamof_asset_field();
    default:
      assertUnreachable(assetType);
      return '';
  }
}

function mapGraphDataQualityWarningToUpstreamRenderIndicatorAssetProps(
  params: {
    readonly warnings: ReadonlyArray<GraphDataQualityWarning>
  } & Pick<RenderIndicatorsBaseProps, 'linksDisabled' | 'newTab' | 'urls'>
): ReadonlyArray<RenderIndicatorAssetProps> {
  const kind = 'graph';
  const groups: Dictionary<RenderIndicatorAssetProps> = {};

  params.warnings.filter(t => t.isActive).map(w => {
    return {
      remoteType: w.asset?.__typename === 'Column' ? w.asset.remoteType : undefined,
      assetName: assetName({ kind, asset: { ...w } }),
      assetType: assetType({ kind, asset: { ...w } }),
      assetUrl: assetUrl(params.urls, { kind, asset: { ...w } }),
      indicators: [mapRenderWarningsToIndicatorProps({ ...w, linksDisabled: params.linksDisabled, newTab: params.newTab })]
    } as RenderIndicatorAssetProps
  }).forEach(i => {
    const name = i.assetName ?? '';
    if (groups[name]) {
      groups[name]?.indicators.push(...i.indicators)
    } else {
      groups[name] = i;
    }
  });

  return filterOutNullAndUndefined(Object.keys(groups).map(k => {
    const g = groups[k];

    if (!g) {
      return null;
    }

    return {
      ...g,
      indicators: g.indicators.sort(t => t.kind === 'severe-warning' ? -1 : 1)
    }
  }));
}

function mapRenderWarningsToIndicatorProps(props: GraphDataQualityWarning & Pick<RenderIndicatorsBaseProps, 'linksDisabled' | 'newTab'>): RenderIndicatorProps {
  return {
    linksDisabled: props.linksDisabled,
    newTab: props.newTab,
    author: {
      name: props.author?.name || props.authorDisplayName,
      username: props.author?.username ?? null,
      domain: props.author?.domain ?? null
    },
    localizedDate: Formatters.formatMediumDateTimeWithoutSeconds(new Date(props.updatedAt)),
    message: props.message,
    type: dataQualityType(props),
    kind: dataQualityKind(props),
    id: props.id,
  }
}

function mapRenderWarningsToIndicatorAssetProps(props: WarningStatusProps & RenderIndicatorsBaseProps): RenderIndicatorAssetProps {
  const asset = props.kind === 'graph' ? {
    kind: 'graph',
    asset: props.anchoredAsset,
  } as PickGraphAssetFields : {
    kind: 'vizportal',
    asset: props.anchoredAsset,
  } as PickVizportalAssetFields;

  const partial: Omit<RenderIndicatorAssetProps, 'indicators'> = {
    subjectArea: props.subjectArea,
    assetName: assetName(asset),
    assetType: assetType(asset),
    assetUrl: assetUrl(props.urls, asset),
    remoteType: (props.kind === 'graph' && props.anchoredAsset?.asset?.__typename === 'Column') ? props.anchoredAsset.asset.remoteType : undefined,
  }

  switch (props.kind) {
    case 'graph': {
      return {
        ...partial,
        indicators: props.anchoredWarnings.filter(t => t.isActive).map(t => mapFragmentOrIDataQualityIndicatorToRenderIndicatorProps({ kind: 'graph', ...t }))
      };
    }
    case 'vizportal': {
      return {
        ...partial,
        indicators: props.anchoredWarnings.filter(t => t.isActive).map(t => mapFragmentOrIDataQualityIndicatorToRenderIndicatorProps({ kind: 'vizportal', ...t })),
      };
    }
    default:
      assertUnreachable(props);
      return {} as RenderIndicatorsProps;
  }
}

/**
 * @deprecated TFS-1478812
*/
export function mapRenderWarningsToIndicatorsProps(props: WarningStatusProps & RenderIndicatorsBaseProps): RenderIndicatorsProps {
  const asset = mapRenderWarningsToIndicatorAssetProps(props);

  const upstream = mapGraphDataQualityWarningToUpstreamRenderIndicatorAssetProps({
    warnings: props.upstreamWarnings.filter(t => t.isActive),
    urls: props.urls,
    linksDisabled: props.linksDisabled,
    newTab: props.newTab,
  });

  const childrenIndicatorAndAsset = mapGraphDataQualityWarningToUpstreamRenderIndicatorAssetProps({
    warnings: props.anchoredChildWarnings?.filter(t => t.isActive) ?? [],
    urls: props.urls,
    linksDisabled: props.linksDisabled,
    newTab: props.newTab,
  });

  return { ...asset, childrenIndicatorAndAsset, upstream }
}

/**
 * @deprecated TFS-1478812
*/
export function mapRenderCertificationsToIndicatorProps(props: CertificationStatusProps & RenderIndicatorsBaseProps): RenderIndicatorsProps {
  const asset = props.kind === 'graph' ? {
    kind: 'graph',
    asset: props.anchoredAsset,
  } as PickGraphAssetFields : {
    kind: 'vizportal',
    asset: props.anchoredAsset,
  } as PickVizportalAssetFields

  const partial: Omit<RenderIndicatorAssetProps, 'indicators'> = {
    subjectArea: props.subjectArea,
    assetName: assetName(asset),
    assetType: assetType(asset),
    assetUrl: assetUrl(props.urls, asset),
  }

  switch (props.kind) {
    case 'graph':
      return {
        ...partial,
        linksDisabled: props.linksDisabled,
        indicators: props.anchoredCertifications.filter(t => t.isActive).map(t => mapFragmentOrIDataQualityIndicatorToRenderIndicatorProps({ kind: 'graph', ...t })),
        childrenIndicatorAndAsset: [],
        upstream: [],
      };
    case 'vizportal':
      return {
        ...partial,
        linksDisabled: props.linksDisabled,
        indicators: props.anchoredCertifications.filter(t => t.isActive).map(t => mapFragmentOrIDataQualityIndicatorToRenderIndicatorProps({ kind: 'vizportal', ...t })),
        childrenIndicatorAndAsset: [],
        upstream: [],
      };
    default:
      assertUnreachable(props);
      return {} as RenderIndicatorsProps;
  }
}

function dataQualityKind(fragment: DataQualityCertificationFragment | DataQualityWarningFragment): DataQualityIndicatorKind {
  switch (fragment.__typename) {
    case 'DataQualityCertification':
      return 'certification';
    case 'DataQualityWarning':
      return fragment.isSevere ? 'severe-warning' : 'standard-warning';
    default:
      assertUnreachable(fragment);
      return 'certification'
  }
}

function dataQualityKindFromVizportal(fragment: IDataQualityIndicator): DataQualityIndicatorKind {
  const type = stringToDataQualityType(fragment.type);
  if (type === DataQualityType.CERTIFIED) {
    return 'certification';
  } else {
    if (fragment.isSevere) {
      return 'severe-warning';
    } else {
      return 'standard-warning';
    }
  }
}

function dataQualityType(fragment: DataQualityCertificationFragment | DataQualityWarningFragment): DataQualityType {
  switch (fragment.__typename) {
    case 'DataQualityCertification':
      return DataQualityType.CERTIFIED;
    case 'DataQualityWarning':
      return stringToDataQualityType(fragment.warningType);
    default:
      assertUnreachable(fragment);
      return DataQualityType.WARNING;
  }
}

function mapFragmentToRenderIndicatorProps(fragment: (DataQualityCertificationFragment | DataQualityWarningFragment) & Pick<RenderIndicatorProps, 'newTab' | 'linksDisabled'> & {
  readonly kind: 'graph';
}): RenderIndicatorProps {
  return {
    linksDisabled: fragment.linksDisabled,
    newTab: fragment.newTab,
    author: {
      name: fragment.author?.name || fragment.authorDisplayName,
      username: fragment.author?.username ?? null,
      domain: fragment.author?.domain ?? null
    },
    localizedDate: Formatters.formatMediumDateTimeWithoutSeconds(new Date(fragment.updatedAt)),
    message: fragment.message,
    type: dataQualityType(fragment),
    kind: dataQualityKind(fragment),
    id: fragment.id,
  }
}

function mapVizportalToRenderIndicatorProps(fragment: IDataQualityIndicator & Pick<RenderIndicatorProps, 'newTab' | 'linksDisabled'> & {
  readonly kind: 'vizportal';
}): RenderIndicatorProps {
  return {
    linksDisabled: fragment.linksDisabled,
    newTab: fragment.newTab,
    author: {
      name: fragment.owner.displayName,
      username: fragment.owner.username,
      domain: fragment.owner.domainName || null
    },
    localizedDate: fragment.updatedAt ? Formatters.formatMediumDateTimeWithoutSeconds(new Date(fragment.updatedAt)) : '',
    message: fragment.message,
    type: stringToDataQualityType(fragment.type),
    kind: dataQualityKindFromVizportal(fragment),
    id: fragment.id,
  }
}

function mapFragmentOrIDataQualityIndicatorToRenderIndicatorProps(fragment:
  (
    ({ kind: 'vizportal' } & IDataQualityIndicator) |
    ({ kind: 'graph' } & (DataQualityCertificationFragment | DataQualityWarningFragment))
  ) & Pick<RenderIndicatorProps, 'newTab' | 'linksDisabled'>
): RenderIndicatorProps {
  switch (fragment.kind) {
    case 'graph':
      return mapFragmentToRenderIndicatorProps(fragment);
    case 'vizportal':
      return mapVizportalToRenderIndicatorProps(fragment);
    default:
      assertUnreachable(fragment);
      return {} as RenderIndicatorProps;
  }
}

type PickGraphAssetFields = {
  readonly kind: 'graph';
  readonly asset: DataQualityWarningAssetFragment | DataQualityCertificationAssetFragment | null;
}

type PickVizportalAssetFields = {
  readonly kind: 'vizportal';
  readonly asset: AssetMarkedByIndicator | null;
}

function assetName(props: PickGraphAssetFields | PickVizportalAssetFields): string {
  switch (props.kind) {
    case 'graph':
      return props.asset?.asset?.name ?? '';
    case 'vizportal':
      return props.asset?.assetName ?? '';
    default:
      assertUnreachable(props);
      return '';
  }
}

function assetType(props: PickGraphAssetFields | PickVizportalAssetFields): DataQualityIndicatorAssetType | null {
  switch (props.kind) {
    case 'graph':
      return props.asset?.asset?.__typename || null;
    case 'vizportal':
      return props.asset?.assetType || null;
    default:
      assertUnreachable(props);
      return null;
  }
}

function assetUrl(urls: SiteUrls | undefined, fragment: PickGraphAssetFields | PickVizportalAssetFields): string | null {
  if (!urls) {
    return null;
  }

  if (fragment.kind === 'vizportal') {
    return fragment.asset?.assetUrl(urls) || '';
  }

  const asset = fragment.asset?.asset;

  if (!asset) {
    return '';
  }

  switch (asset.__typename) {
    case 'Column':
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      return asset.table?.__typename === 'DatabaseTable' ? urls.catalogTableColumnUrl(asset.table.id, asset.id) : '';
    case 'Flow':
      return urls.flowUrl(asset.vizportalUrlId);
    case 'PublishedDatasource':
      return urls.datasourceUrl(asset.vizportalUrlId);
    case 'DatabaseServer':
    case 'CloudFile':
    case 'File':
    case 'WebDataConnector':
      return urls.catalogDatabaseUrl(asset.id);
    case 'VirtualConnectionTable':
    case 'DatabaseTable':
      return urls.catalogTableUrl(asset.id);
    case 'VirtualConnection':
      return urls.virtualConnectionUrl(asset.id);
    default:
      assertUnreachable(asset);
      return '';
  }
}
